/**
 * @file CheckboxListPicker.tsx
 *
 * Filterable checkbox list used by the product form to pick categories and
 * related products. Supports a one-level parent/child hierarchy: toggling a
 * parent cascades to its descendants, and a partially-selected parent shows the
 * mixed (indeterminate) state. A filter bar narrows the list to All, Selected,
 * or Unselected and offers a clear-all. Controlled: the current selection is
 * owned by the parent and reported via `onSelectionChange`. Mirrors WSB-v2's
 * picker (Gravity `ChipRadio` filter, `Action` clear-all, zebra rows with a
 * Gravity `Checkbox` + thumbnail).
 */

import { useMemo, useState } from 'react';
import { Action, ChipRadio, Checkbox, Icon } from '@group.one/react-gravity';
import { ImageOrDefault } from './ImageOrDefault';
import { useMediaQuery } from '../hooks/use-media-query';
import styles from './CheckboxListPicker.module.css';

/** A selectable row. `parent` is `""` (or an unknown id) for top-level items. */
export interface CheckboxListPickerItem {
  id: string;
  name: string;
  parent: string;
  image?: { src: string } | null;
}

type FilterMode = 'all' | 'selected' | 'unselected';

interface CheckboxListPickerProps {
  items: CheckboxListPickerItem[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  allLabel: string;
  selectedLabel: string;
  unselectedLabel: string;
  clearLabel: string;
  noResultsLabel: string;
  noResultsDescriptionLabel: string;
  expandLabel: string;
  collapseLabel: string;
  /** `data-testid` namespace for the picker; defaults to `checkbox-list-picker`. */
  testId?: string;
}

/**
 * Renders the filterable checkbox picker.
 *
 * @param props - Items, current selection, change handler, and localized copy.
 *
 * @returns The picker element.
 */
export function CheckboxListPicker({
  items,
  selectedIds,
  onSelectionChange,
  allLabel,
  selectedLabel,
  unselectedLabel,
  clearLabel,
  noResultsLabel,
  noResultsDescriptionLabel,
  expandLabel,
  collapseLabel,
  testId = 'checkbox-list-picker',
}: CheckboxListPickerProps) {
  const [mode, setMode] = useState<FilterMode>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const isMobile = useMediaQuery('Mobile');

  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  const childrenByParent = useMemo(() => {
    const map = new Map<string, CheckboxListPickerItem[]>();
    const ids = new Set(items.map((i) => i.id));
    for (const item of items) {
      if (item.parent && ids.has(item.parent)) {
        const list = map.get(item.parent) ?? [];
        list.push(item);
        map.set(item.parent, list);
      }
    }
    return map;
  }, [items]);

  const topLevel = useMemo(
    () => items.filter((i) => !i.parent || !items.some((p) => p.id === i.parent)),
    [items],
  );

  const descendants = (id: string): string[] => {
    const out: string[] = [];
    for (const child of childrenByParent.get(id) ?? []) {
      out.push(child.id, ...descendants(child.id));
    }
    return out;
  };

  const matches = (item: CheckboxListPickerItem): boolean => {
    if (mode === 'selected') {
      return selected.has(item.id);
    }
    if (mode === 'unselected') {
      return !selected.has(item.id);
    }
    return true;
  };

  const toggle = (item: CheckboxListPickerItem): void => {
    const next = new Set(selected);
    const ids = [item.id, ...descendants(item.id)];
    const willSelect = !selected.has(item.id);
    for (const id of ids) {
      if (willSelect) {
        next.add(id);
      } else {
        next.delete(id);
      }
    }
    onSelectionChange([...next]);
  };

  const toggleExpand = (id: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const filtering = mode !== 'all';

  const renderRow = (item: CheckboxListPickerItem, depth: number, index: number) => {
    const kids = childrenByParent.get(item.id) ?? [];
    const visibleKids = kids.filter(matches);
    const isOpen = expanded.has(item.id) || filtering;
    const childIds = descendants(item.id);
    const mixed = !selected.has(item.id) && childIds.some((id) => selected.has(id));
    const hasChildren = kids.length > 0;
    const showSelf = matches(item) || visibleKids.length > 0;
    if (!showSelf) {
      return null;
    }
    return (
      <div key={item.id}>
        <div
          className={`${styles.row} ${index % 2 === 0 ? styles.rowOdd : styles.rowEven} gv-flex gv-items-center gv-gap-sm gv-p-md`}
          style={depth > 0 ? { paddingLeft: `${12 + depth * 20}px` } : undefined}
          data-testid={`${testId}-item`}
          data-id={item.id}
        >
          <Checkbox
            checked={selected.has(item.id)}
            mixed={mixed}
            onChange={() => toggle(item)}
            className="gv-flex-shrink-0"
            condensed
          />
          <ImageOrDefault
            src={item.image?.src}
            alt={item.name}
            className={`${styles.thumbnail} gv-border gv-radius`}
          />
          <span
            title={item.name}
            className="gv-text-truncate gv-flex-1 gv-text-sm"
            onClick={() => hasChildren && toggleExpand(item.id)}
            onKeyDown={(event) => {
              if (hasChildren && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                toggleExpand(item.id);
              }
            }}
            role={hasChildren ? 'button' : undefined}
            tabIndex={hasChildren ? 0 : undefined}
          >
            {item.name}
          </span>
          {hasChildren && (
            <button
              type="button"
              className="gv-ml-auto"
              aria-label={isOpen ? collapseLabel : expandLabel}
              aria-expanded={isOpen}
              onClick={() => toggleExpand(item.id)}
            >
              <Icon
                name={isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                size="small"
              />
            </button>
          )}
        </div>
        {isOpen && hasChildren && (
          <div>{kids.map((child) => renderRow(child, depth + 1, index))}</div>
        )}
      </div>
    );
  };

  const rows = topLevel
    .map((item, index) => renderRow(item, 0, index))
    .filter((row) => row !== null);

  return (
    <div className="gv-flex gv-flex-col gv-gap-md gv-w-full" data-testid={testId}>
      <div
        className={`gv-flex gv-items-center gv-gap-sm ${isMobile ? 'gv-flex-wrap' : ''}`}
      >
        {(
          [
            ['all', allLabel],
            ['selected', selectedLabel],
            ['unselected', unselectedLabel],
          ] as [FilterMode, string][]
        ).map(([value, label]) => (
          <ChipRadio
            key={value}
            active={mode === value}
            onClick={() => setMode(value)}
          >
            {label}
          </ChipRadio>
        ))}
        {selectedIds.length > 0 && (
          <Action component="button" onClick={() => onSelectionChange([])}>
            <span className="gv-text-sm gv-text-bold">{clearLabel}</span>
          </Action>
        )}
      </div>

      <div className={`${styles.listContainer} gv-radius gv-border`}>
        {rows.length > 0 ? (
          rows
        ) : (
          <div
            className={`${styles.noItems} gv-flex gv-flex-col gv-items-center gv-justify-center gv-w-full gv-text-sm gv-text-center gv-p-lg`}
          >
            <span>{noResultsLabel}</span>
            <span>{noResultsDescriptionLabel}</span>
          </div>
        )}
      </div>
    </div>
  );
}
